/**
 * Which of the six things a list is currently saying.
 *
 * Every list screen in this console used to say one of two: rows, or an empty table with headers. An
 * empty table is the worst possible answer because it is the same picture for six situations that
 * need six different responses - the read failed, the read has not finished, the fleet has never
 * reported, the filter excludes everything, there is genuinely nothing to do, or here are the rows.
 *
 * The rule this encodes is D-263, and it is encoded once here so every screen gets it rather than
 * each screen getting it right separately: **a value that was not measured is never reported as a
 * value that was.** `value === null` means the read did not arrive and is `error`, never `good-news`.
 * The dashboard once printed *Every machine is covered* beside an expired session, and this is the
 * shape of the fix generalised.
 *
 * `good-news` is the state worth having copy for and the one most easily got wrong. "Nothing is
 * waiting" is an achievement, and drawing it the way an error is drawn tells an operator their
 * console is broken at the moment it is telling them their fleet is fine.
 */
export type EmptyStateKind = 'loading' | 'error' | 'first-run' | 'filtered' | 'good-news' | 'rows'

export interface ListState<TRow> {
  /** Null when the read did not arrive. An empty array means it arrived and found nothing. */
  value: readonly TRow[] | null
  error: string | null
  loading: boolean
  /** Whether the operator has narrowed the list. An empty filtered list has a different fix. */
  filtered: boolean
  /**
   * Whether this tenant has ever had anything of this kind, where the screen can tell.
   *
   * Distinguishes "no devices have ever reported" from "every case is closed". Both are empty lists
   * and only one of them is good news; a screen that cannot tell should pass `false` and get the
   * first-run copy, which is the safer of the two to be wrong about.
   */
  everHadRows?: boolean
}

export function emptyStateFor<TRow>(state: ListState<TRow>): EmptyStateKind {
  // Error before loading: a failed read that is being retried is still a failed read, and the
  // spinner would hide the reason.
  if (state.error !== null) return 'error'
  if (state.value === null) return state.loading ? 'loading' : 'error'
  if (state.value.length > 0) return 'rows'
  if (state.filtered) return 'filtered'
  return state.everHadRows === true ? 'good-news' : 'first-run'
}

export interface EmptyStateCopy {
  /** The sentence. Says what is true, not what went wrong with the request. */
  message: string
  /** What to do about it, when there is something. */
  hint?: string
  /** The label of the one action offered, if any. */
  action?: string
}

/**
 * What each state says, per screen.
 *
 * Copy rather than a component, so it is data a reviewer can read in one place and so the same
 * screen cannot say two different things in two different branches. The error message is the pattern
 * worth copying: **what failed, what is still true, and one retry.** "Could not load cases" alone
 * invites the reading that the fleet has stopped reporting, which is the expensive misunderstanding.
 */
export const CASES_COPY: Record<Exclude<EmptyStateKind, 'rows'>, EmptyStateCopy> = {
  loading: { message: 'Loading cases…' },
  error: {
    message: 'Could not load cases.',
    hint: 'The agents are still reporting — this is the console, not the fleet.',
    action: 'Retry'
  },
  'first-run': {
    message: 'No cases yet.',
    hint: 'The agent reports within a minute of a device first checking in.'
  },
  filtered: { message: 'No cases match this query.', action: 'Clear filters' },
  'good-news': {
    message: 'Nothing is waiting.',
    hint: 'Every case has been decided. New ones appear here as the fleet reports them.'
  }
}

export const DEVICES_COPY: Record<Exclude<EmptyStateKind, 'rows'>, EmptyStateCopy> = {
  loading: { message: 'Loading devices…' },
  error: {
    message: 'Could not load the fleet.',
    hint: 'The devices are still checking in — this is the console, not the fleet.',
    action: 'Retry'
  },
  'first-run': {
    message: 'No device has enrolled yet.',
    hint: 'Deployment has the kit an installer takes to a machine, and the token it needs.'
  },
  filtered: { message: 'No device matches this search.', action: 'Clear search' },
  'good-news': { message: 'No device matches this search.', action: 'Clear search' }
}

export const POLICIES_COPY: Record<Exclude<EmptyStateKind, 'rows'>, EmptyStateCopy> = {
  loading: { message: 'Loading policies…' },
  error: {
    message: 'Could not load policies.',
    hint: 'Whatever the fleet is already running is unaffected — this is the console.',
    action: 'Retry'
  },
  'first-run': {
    message: 'No policies yet.',
    hint: 'A policy says when to run a capability, under what condition, and what to do with the result.'
  },
  filtered: { message: 'No policy matches this search.', action: 'Clear search' },
  'good-news': { message: 'No policy matches this search.', action: 'Clear search' }
}

/**
 * What the Enrollment list says in each of its states.
 *
 * The error copy is the one that matters and it is the reason this table is not shared with the
 * others. A failure on this tab looks exactly like a fleet outage - the list of every way into the
 * organization is empty - and it is not one. Machines keep enrolling with tokens they already hold
 * whether or not this console can draw them (D-442).
 */
export const WAYS_IN_COPY: Record<Exclude<EmptyStateKind, 'rows'>, EmptyStateCopy> = {
  loading: { message: 'Loading kits and tokens…' },
  error: {
    message: 'Could not load kits and tokens.',
    hint: 'Machines can still enroll and the fleet is still reporting — this is the console, not the server they talk to. Nothing has been revoked or changed.',
    action: 'Retry'
  },
  'first-run': {
    message: 'No kits or tokens yet.',
    hint: 'Build a kit and hand it to whoever is at the machines. Each one that runs it appears on the Fleet tab within a minute of its first check-in.'
  },
  filtered: {
    message: 'Nothing here under this filter.',
    hint: 'Other kits and tokens are here under the other filters.',
    action: 'Show everything'
  },
  'good-news': {
    message: 'Nothing here under this filter.',
    hint: 'Other kits and tokens are here under the other filters.',
    action: 'Show everything'
  }
}
